/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Grid2,
  Backdrop,
  CircularProgress,
  Toolbar,
  Modal,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InputAdornment from "@mui/material/InputAdornment";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Topheader from "../../Layout/header/header";
import { fetchOverViewDetails } from "../../Services/redux/overView/OverViewDetailsActions";
import { DataGrid } from "@mui/x-data-grid";
import "./oem-dashboard.css";
import DownloadReportPopup from "./download-report-popup";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useNavigate } from "react-router-dom";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { OEMDrawerFilter } from "./oem-filter/oem-drawer-filter";

export default function OEMDashboard() {
  const dispatch = useDispatch();
  const [appData, setAppData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [appSelected, setAppSelected] = useState("");
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState(false);
  const [categoryObjectList, setCategoryObjectList] = useState([]);
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [modelObjectList, setModelObjectList] = useState([]);
  const [channelSelections, setChannelSelections] = useState({
    All: [],
    "OEM Apps": [],
    "3rd Party Apps": [],
  });
  const [channelListchecked, setChannelListchecked] = useState([]);

  const [categorySelection, setCategorySelection] = useState({});
  const [modelSelections, setModelSelections] = useState({});
  const [paymentSelections, setPaymentSelections] = useState({});
  const [countrySelections, setCountrySelections] = useState({});
  const [statusSelections, setStatusSelections] = useState({});
  const [apiLevelSelections, setApiLevelSelections] = useState({});
  const [ecuTypeSelections, setEcuTypeSelections] = useState({});
  const [openPopUp, setOpenPopUp] = useState(false);
  const [isFilterTriggered, setIsFilterTriggered] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [portfolioError, setPortfolioError] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRestoringFromSession, setIsRestoringFromSession] = useState(false);

  const [originalCounts, setOriginalCounts] = useState({
    appsApprovedByOem: 0,
    appsApprovedFromHarman: 0,
    appsPerCategory: {},
    appsPerModel: {},
  });

  const fetchOverList = useSelector(
    (state) => state.overViewList.overviewDetails
  );

  const [currentRequestBody, setCurrentRequestBody] = useState({
    categories: ["ALL"],
    ecuTypes: ["ALL"],
    models: ["ALL"],
    countries: ["ALL"],
    portfolioMgrReviewStatusList: [
      "APPROVED_FOR_BETA",
      "STAGING_APPROVAL_IN_REVIEW",
      "STAGING_APPROVAL_APPROVED",
      "PRODUCTION_APPROVAL_IN_REVIEW",
      "PRODUCTION_APPROVAL_APPROVED",
    ],
    contentStatusList: ["APPROVED_FOR_PRODUCTION", "PUBLISHED"],
    androidApiLevel: ["ALL"],
    payments: ["ALL"],
    searchText: "",
  });

  const columns = [
    {
      field: "appIcon",
      headerName: "",
      width: 20,
      // maxWidth: 1000,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <img
            src={params.row.icon}
            alt={params.value}
            width="30"
            height="30"
          />
        </Box>
      ),
    },
    {
      field: "application",
      headerName: "Application",
      className: "headerCls",
      flex: 1,
      sortable: true,
      minWidth: 220,
      maxWidth: 1000,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
            {params.value}
          </Typography>
          {params.row.isOemApp && (
            <Box
              sx={{
                backgroundColor: "#E8E8E8",
                color: "#000",
                padding: "0px 6px",
                borderRadius: "4px",
                fontSize: "8px",
                fontWeight: "bold",
                width: "fit-content",
              }}
            >
              OEM
            </Box>
          )}
        </Box>
      ),
    },

    {
      field: "category",
      headerName: "Category",
      flex: 1,
      sortable: true,
      minWidth: 220,
      maxWidth: 1000,
    },
    {
      field: "published",
      headerName: "Published",
      headerAlign: "center",
      flex: 1,
      minWidth: 90,
      maxWidth: 1000,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "normal" }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "API",
      headerName: "API",
      minWidth: 90,
      maxWidth: 1000,
      headerAlign: "center",
      flex: 1,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            marginLeft: "10px",
          }}
        >
          API
        </Box>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "normal" }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "lastUpdated",
      headerName: "Last Modified",
      flex: 1,
      minWidth: 150,
      maxWidth: 1000,
      sortable: true,
    },
    {
      field: "appPricing",
      headerName: "App Pricing",
      minWidth: 135,
      maxWidth: 1000,
      flex: 1,
      sortable: true,
    },
  ];

  const handleRowSelected = (selectedRow) => {
    if (selectedRow.length !== 0) {
      navigate(`/ui/oem-admin/dashboard/application/${selectedRow[0]}`);
      setAppSelected(selectedRow[0]);
    }
  };

  useEffect(() => {
    // This useEffect can be used for any component initialization if needed
  }, []);

  // Initial portfolio check on component mount
  useEffect(() => {
    const checkInitialPortfolio = () => {
      const portfolioId = sessionStorage.getItem("portfolioId");
      console.log("Initial portfolio check:", portfolioId);

      if (!portfolioId || portfolioId === "null") {
        console.log(
          "No portfolio found on mount, waiting before showing error..."
        );
        setIsInitialLoad(true);

        // Wait for 3 seconds before showing error message to give time for portfolio to load
        const initialDelay = setTimeout(() => {
          const currentPortfolio = sessionStorage.getItem("portfolioId");
          if (!currentPortfolio || currentPortfolio === "null") {
            console.log(
              "Portfolio still not available after initial delay, showing error"
            );
            setPortfolioError(true);
            setLoader(true);
          } else {
            setIsInitialLoad(false);
          }
        }, 3000);

        // Set up polling for portfolio availability
        const pollInterval = setInterval(() => {
          const currentPortfolio = sessionStorage.getItem("portfolioId");
          if (currentPortfolio && currentPortfolio !== "null") {
            console.log("Portfolio found during polling:", currentPortfolio);
            setPortfolioError(false);
            setIsInitialLoad(false);
            clearInterval(pollInterval);
            clearTimeout(initialDelay);
            // Portfolio is now available, the main useEffect will handle the API call
          }
        }, 1000);

        // Stop polling after 30 seconds
        setTimeout(() => {
          clearInterval(pollInterval);
          clearTimeout(initialDelay);
          if (
            !sessionStorage.getItem("portfolioId") ||
            sessionStorage.getItem("portfolioId") === "null"
          ) {
            console.error("Portfolio still not available after 30 seconds");
            setLoader(false);
            setIsInitialLoad(false);
          }
        }, 30000);

        return () => {
          clearInterval(pollInterval);
          clearTimeout(initialDelay);
        };
      } else {
        // Portfolio is available immediately
        setPortfolioError(false);
        setIsInitialLoad(false);
      }
    };

    checkInitialPortfolio();
  }, []);

  // Early check for saved filter state - runs immediately on component mount
  useEffect(() => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (portfolioId && portfolioId !== "null") {
      const filterStateKey = `oemFilterState_${portfolioId}`;
      const savedFilterState = sessionStorage.getItem(filterStateKey);

      if (savedFilterState) {
        try {
          const filterState = JSON.parse(savedFilterState);
          console.log(
            "Early check: Found saved filter state for portfolio:",
            portfolioId
          );

          // Check if the saved state is not too old (24 hours)
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          if (Date.now() - filterState.timestamp > maxAge) {
            console.log("Saved filter state is too old, clearing it");
            sessionStorage.removeItem(filterStateKey);
            return;
          }

          // Set the currentRequestBody to the saved filter data immediately
          if (filterState.lastAppliedFilterData) {
            console.log(
              "Setting currentRequestBody to saved filter data immediately"
            );
            setCurrentRequestBody(filterState.lastAppliedFilterData);
            setIsFilterTriggered(true);
          }
        } catch (error) {
          console.error(
            "Failed to parse saved filter state in early check:",
            error
          );
          sessionStorage.removeItem(filterStateKey);
        }
      }
    }
  }, []);

  // Load and apply saved filter state on dashboard mount to restore filtered data
  useEffect(() => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (
      portfolioId &&
      portfolioId !== "null" &&
      !isInitialLoad &&
      !isRestoringFromSession
    ) {
      const filterStateKey = `oemFilterState_${portfolioId}`;
      const savedFilterState = sessionStorage.getItem(filterStateKey);

      if (savedFilterState) {
        try {
          const filterState = JSON.parse(savedFilterState);
          console.log(
            "Loading saved filter state on dashboard mount for portfolio:",
            portfolioId
          );

          // Check if the saved state is not too old (24 hours)
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          if (Date.now() - filterState.timestamp > maxAge) {
            console.log(
              "Saved filter state is too old on dashboard mount, clearing it"
            );
            sessionStorage.removeItem(filterStateKey);
            return;
          }

          // Only apply saved filter if we have the data and we're not already showing filtered data
          if (filterState.lastAppliedFilterData) {
            setIsRestoringFromSession(true);
            console.log(
              "Auto-applying saved filter data on dashboard mount to restore filtered results"
            );

            // Apply the filter immediately to ensure data is restored properly
            dispatch(
              fetchOverViewDetails({
                isAppListNeeded: true,
                isCountNeeded: String(true).trim(),
                isAppCountNeeded: true,
                offset: 1,
                limit: pageSize,
                searchText: searchQuery,
                requestBody: filterState.lastAppliedFilterData,
              })
            )
              .then(() => {
                console.log(
                  "Successfully restored filtered data from sessionStorage"
                );
              })
              .catch((error) => {
                console.error("Error restoring filtered data:", error);
              })
              .finally(() => {
                setIsRestoringFromSession(false);
              });
          }
        } catch (error) {
          console.error(
            "Failed to parse saved filter state on dashboard mount:",
            error
          );
          sessionStorage.removeItem(filterStateKey);
          setIsRestoringFromSession(false);
        }
      }
    }
  }, [isInitialLoad]); // Only depend on isInitialLoad to avoid multiple calls

  const toggleDrawer = (newOpen) => () => {
    setOpenFilter(newOpen);
  };

  const handleFilterApiCalls = (props) => {
    setIsFilterTriggered(true);
    setCurrentRequestBody(props);
    setPage(0);

    dispatch(
      fetchOverViewDetails({
        isAppListNeeded: true,
        isCountNeeded: String(true).trim(),
        isAppCountNeeded: true,
        offset: 1,
        limit: pageSize,
        searchText: searchQuery,
        requestBody: props,
      })
    )
      .then(() => {
        // Only close filter drawer if not restoring from session
        if (!isRestoringFromSession) {
          setOpenFilter(false);
        }
      })
      .catch((error) => {
        console.error("Error applying filter:", error);
        // Don't close filter drawer on error so user can retry
      });
  };
  useEffect(() => {
    // Check if pageSize is set and portfolio is available
    if (!pageSize) return;

    // Don't proceed if we're still in initial load phase
    if (isInitialLoad) return;

    // Don't fetch if we're currently restoring from session storage
    if (isRestoringFromSession) return;

    const fetchData = async () => {
      setLoader(true); // Show loader
      setPortfolioError(false);

      const requestBody = {
        ...currentRequestBody,
        searchText: searchQuery, // Update search text from current state
      };

      try {
        await Promise.resolve(
          dispatch(
            fetchOverViewDetails({
              isAppListNeeded: true,
              isCountNeeded: String(true).trim(),
              isAppCountNeeded: true,
              offset: page + 1,
              limit: pageSize,
              searchText: searchQuery,
              requestBody,
            })
          )
        );
        setRetryCount(0); // Reset retry count on success
      } catch (error) {
        console.error("Error fetching overview details:", error);

        // Handle 400 errors which might be related to portfolio issues
        if (error.status === 400) {
          console.log("Received 400 error, might be portfolio related");
          setPortfolioError(true);

          // Retry after a short delay if retry count is low
          if (retryCount < 3) {
            setRetryCount((prev) => prev + 1);
            setTimeout(() => {
              const currentPortfolio = sessionStorage.getItem("portfolioId");
              if (currentPortfolio && currentPortfolio !== "null") {
                fetchData();
              }
            }, 2000);
          }
        }
      } finally {
        setLoader(false); // Hide loader after dispatch completes
      }
    };

    const portfolioId = sessionStorage.getItem("portfolioId");

    // If portfolio is null or not set, wait for it to be available
    if (!portfolioId || portfolioId === "null") {
      console.log("Portfolio not available yet, waiting...");
      setPortfolioError(true);

      // Set up a polling mechanism to check for portfolio availability
      const checkPortfolio = setInterval(() => {
        const currentPortfolio = sessionStorage.getItem("portfolioId");
        if (currentPortfolio && currentPortfolio !== "null") {
          console.log("Portfolio is now available:", currentPortfolio);
          setPortfolioError(false);
          clearInterval(checkPortfolio);

          // Trigger the fetch after portfolio is available
          fetchData();
        }
      }, 1000);

      // Clear interval after 15 seconds to avoid infinite polling
      setTimeout(() => {
        clearInterval(checkPortfolio);
        if (
          !sessionStorage.getItem("portfolioId") ||
          sessionStorage.getItem("portfolioId") === "null"
        ) {
          console.error("Portfolio still not available after 15 seconds");
          setPortfolioError(true);
          setLoader(false);
        }
      }, 15000);

      return () => clearInterval(checkPortfolio);
    } else {
      // Portfolio is available, fetch data immediately
      fetchData();
    }
  }, [
    dispatch,
    page,
    pageSize,
    searchQuery,
    retryCount,
    isInitialLoad,
    isRestoringFromSession,
  ]);

  useEffect(() => {
    const processAppData = async () => {
      try {
        // Only proceed if fetchOverList exists and has the required structure
        await new Promise((resolve) => setTimeout(resolve, 300));
        const appBriefs = await fetchOverList?.catalogOverviewAppDTO
          ?.catalogOverviewAppBriefs;
        console.log("appBriefs:", appBriefs);
        if (Array.isArray(appBriefs) && appBriefs.length > 0) {
          const formattedData = await Promise.resolve(
            appBriefs.map((app) => ({
              id: app.id,
              application: app.title,
              isOemApp: app.isOemApp,
              icon: app.icon,
              published: app.appStatus === "PUBLISHED" ? "Yes" : "No",
              category: app.categories?.join(", ") || "N/A",
              API: app.androidApiLevel,
              lastUpdated: new Date(app.lastUpdateTime)
                .toISOString()
                .split("T")[0],
              appPricing:
                (app.isInAppPurchase && app.isOemApp) || app.isPremiumApp
                  ? "Premium"
                  : "Free",
            }))
          );
          setAppData(formattedData);
          setOpenPopUp(false);
        } else {
          console.log("appBriefs:", appBriefs);
          await Promise.resolve(setAppData([]));
          setOpenPopUp(true);
        }

        // Handle portfolio and storage operations asynchronously
        const portfolioId = sessionStorage.getItem("portfolioId");
        const storageKey = `originalCounts_${portfolioId}`;

        if (fetchOverList?.catalogOverviewAppsCount) {
          const storedCounts = await Promise.resolve(
            sessionStorage.getItem(storageKey)
          );

          if (storedCounts) {
            const parsedCounts = await Promise.resolve(
              JSON.parse(storedCounts)
            );
            setOriginalCounts({
              appsApprovedByOem: parsedCounts.appsApprovedByOem,
              appsApprovedFromHarman: parsedCounts.appsApprovedFromHarman,
              appsPerCategory:
                fetchOverList.catalogOverviewAppsCount.appsPerCategory || {},
              appsPerModel:
                fetchOverList.catalogOverviewAppsCount.appsPerModel || {},
            });
          } else {
            // First time loading - store the original counts (without filters)
            const isInitialOrNoFilter =
              currentRequestBody.categories.includes("ALL") &&
              currentRequestBody.ecuTypes.includes("ALL") &&
              currentRequestBody.models.includes("ALL") &&
              currentRequestBody.countries.includes("ALL") &&
              currentRequestBody.androidApiLevel.includes("ALL") &&
              currentRequestBody.payments.includes("ALL") &&
              !currentRequestBody.searchText;

            if (isInitialOrNoFilter) {
              console.log(
                "Storing original counts for portfolio:",
                portfolioId
              );

              const newOriginalCounts = await Promise.resolve({
                appsApprovedByOem:
                  fetchOverList.catalogOverviewAppsCount.appsApprovedByOem || 0,
                appsApprovedFromHarman:
                  fetchOverList.catalogOverviewAppsCount
                    .appsApprovedFromHarman || 0,
                appsPerCategory:
                  fetchOverList.catalogOverviewAppsCount.appsPerCategory || {},
                appsPerModel:
                  fetchOverList.catalogOverviewAppsCount.appsPerModel || {},
              });

              setOriginalCounts(newOriginalCounts);
              await Promise.resolve(
                sessionStorage.setItem(
                  storageKey,
                  JSON.stringify(newOriginalCounts)
                )
              );
            }
          }
        }

        if (fetchOverList?.catalogOverviewAppsCount?.appsPerCategory) {
          setCategoryObjectList(
            fetchOverList?.catalogOverviewAppsCount?.appsPerCategory
          );
        }

        if (fetchOverList?.catalogOverviewAppsCount?.appsPerModel) {
          setModelObjectList(
            fetchOverList?.catalogOverviewAppsCount?.appsPerModel
          );
        }
      } catch (error) {
        console.error("Error processing app data:", error);
        await Promise.resolve(setAppData([]));
      }
    };

    if (fetchOverList?.catalogOverviewAppDTO) {
      processAppData();
    }
  }, [fetchOverList, isFilterTriggered]);

  useEffect(() => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (portfolioId && portfolioId !== "null") {
      const storageKey = `originalCounts_${portfolioId}`;
      const storedCounts = sessionStorage.getItem(storageKey);

      if (storedCounts) {
        console.log(
          "Loading stored original counts for portfolio:",
          portfolioId
        );
        const parsedCounts = JSON.parse(storedCounts);
        setOriginalCounts(parsedCounts);
      }
    }
  }, []);

  return (
    <>
      <Topheader />
      {loader && !isInitialLoad && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {isInitialLoad && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "rgba(255, 255, 255, 0.9)",
            borderRadius: 2,
            p: 3,
            zIndex: 1000,
            boxShadow: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="textSecondary">
            Initializing dashboard...
          </Typography>
        </Box>
      )}

      {portfolioError && !isInitialLoad && (
        <Box
          sx={{
            position: "fixed",
            top: 80,
            right: 20,
            bgcolor: "#fff3cd",
            color: "#856404",
            border: "1px solid #ffeaa7",
            borderRadius: 1,
            p: 2,
            zIndex: 1000,
            boxShadow: 2,
            maxWidth: 300,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "12px" }}>
            Loading portfolio data...
          </Typography>
        </Box>
      )}
      <Toolbar />

      <Box
        sx={{
          px: "200px", // equivalent to px: 28
          "@media (max-width: 1300px)": {
            px: "110px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: "24px",
              lineHeight: "120%",
              letterSpacing: "0%",
            }}
          >
            Overview
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterAltOutlinedIcon
              color="action"
              className="filterIconCls"
              onClick={toggleDrawer(true)}
            />
            <>
              <Button
                variant="contained"
                sx={{
                  fontSize: "12px",
                  backgroundColor: "#121415F2",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: "500",
                  lineHeight: "100%",
                  height: "36px",
                }}
                onClick={() => setIsPopupOpen(true)}
              >
                <FileDownloadOutlinedIcon
                  style={{
                    color: "white",
                    marginTop: 2,
                    width: "14px",
                    height: "14px",
                    marginLeft: "-5px",
                    paddingRight: "6px",
                  }}
                />
                Download Report
              </Button>
              <DownloadReportPopup
                open={isPopupOpen}
                handleClose={() => setIsPopupOpen(false)}
                currentFilters={currentRequestBody}
              />
            </>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            mt: 2,
          }}
        >
          <Box
            sx={{
              width: "30%",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Card
              sx={{
                width: "85%",
                height: "75%",
                textAlign: "center",
                bgcolor: "#fff",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography
                  sx={{
                    fontWeight: "700",
                    // lineHeight: "100%",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    letterSpacing: "1%",
                    backgroundColor: "#E8E8E8",
                  }}
                >
                  Apps Published by OEM
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "600",
                    mt: 3,
                    fontSize: "25px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                 
                  {originalCounts.appsApprovedByOem}
                </Typography>
              </CardContent>
            </Card>
            <Card
              sx={{
                width: "85%",
                height: "75%",
                textAlign: "center",
                bgcolor: "#fff",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography
                  sx={{
                    fontWeight: "700",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    letterSpacing: "1%",
                    backgroundColor: "#E8E8E8",
                  }}
                >
                  Apps Approved from Harman
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "600",
                    mt: 3,
                    fontSize: "25px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                
                  {originalCounts.appsApprovedFromHarman}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Applications Per Category */}
          <Paper
            sx={{
              width: "99%",
              height: "291px",
              overflowY: "auto",
              overflowX: "hidden",
              marginLeft: "-10px",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#00000080",
                borderRadius: "5px",
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: "700",
                bgcolor: "#E8E8E8",
                height: "48px",
                display: "flex",
                alignItems: "center",

                px: 2,

                fontSize: "14px",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              Applications Per Category
            </Typography>

            <Grid2
              container
              spacing={1}
              sx={{ marginLeft: "-2px", marginTop: "10px", columnGap: "3px" }}
            >
              {/* {fetchOverList?.catalogOverviewAppsCount?.appsPerCategory &&
              Object.keys(
                fetchOverList.catalogOverviewAppsCount.appsPerCategory
              ).length > 0 ? (
                Object.entries(
                  fetchOverList.catalogOverviewAppsCount.appsPerCategory
                ) */}
              {originalCounts.appsPerCategory &&
              Object.keys(originalCounts.appsPerCategory).length > 0 ? (
                Object.entries(originalCounts.appsPerCategory)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([category, count]) => (
                    <Grid2
                      size={4}
                      key={category}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        paddingTop: "4px !important",
                        paddingBottom: "4px",
                        maxWidth: "32.3% !important",
                        justifyContent: "center",
                        borderRadius: "4px",
                        paddingRight: "8px",
                        marginLeft: "4px",
                        "&:hover": {
                          bgcolor: "#f5f5f5",
                          "& .count-typography": {
                            fontWeight: "600",
                          },
                        },
                      }}
                    >
                      {/* Category Name */}
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#000000DE",
                        }}
                      >
                        {category}
                      </Typography>

                      {/* Dashed Line */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          borderBottom: "1px dashed #999",
                          mx: 1,
                          minWidth: "20px",
                        }}
                      />

                      {/* Count Value */}
                      <Typography
                        className="count-typography" // Add class for targeting
                        sx={{
                          flexShrink: 0,
                          minWidth: "30px",
                          textAlign: "right",
                          fontSize: "14px",
                        }}
                      >
                        {count}
                      </Typography>
                    </Grid2>
                  ))
              ) : (
                <Typography textAlign="center" sx={{ width: "100%", mt: 2 }}>
                  No Data Available
                </Typography>
              )}
            </Grid2>
          </Paper>
        </Box>
        <Paper sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontWeight: "700",
              bgcolor: "#E8E8E8",
              height: "48px",
              display: "flex",
              alignItems: "center",
              px: 2,
              fontSize: "14px",
              position: "sticky",
              top: 5,
              zIndex: 1,
            }}
          >
            Applications Per Model
          </Typography>
          <Grid2 container spacing={1} sx={{ marginLeft: "1px", mt: 0 }}>
            {/* {fetchOverList?.catalogOverviewAppsCount?.appsPerModel &&
            Object.keys(fetchOverList.catalogOverviewAppsCount.appsPerModel)
              .length > 0 ? (
              Object.entries(
                fetchOverList.catalogOverviewAppsCount.appsPerModel
              ) */}
            {originalCounts.appsPerModel &&
            Object.keys(originalCounts.appsPerModel).length > 0 ? (
              Object.entries(originalCounts.appsPerModel)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([model, count]) => (
                  <Grid2
                    size={4}
                    key={model}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      px: 1,
                      "&:hover": { bgcolor: "#f5f5f5" },
                      fontSize: "12px",
                      lineHeight: "100%",
                      letterSpacing: "1%",
                    }}
                  >
                    <Typography 
                      sx={{ 
                        fontSize: "12px",
                      }}
                    >
                      {model}
                    </Typography>
                    <Box
                      sx={{
                        flexGrow: 1,
                        borderBottom: "1px dashed #999",
                        mx: 1,
                        minWidth: "20px",
                      }}
                    />
                    <Typography
                      sx={{
                        flexShrink: 0,
                        minWidth: "30px",
                        textAlign: "right",
                        fontSize: "12px",
                        lineHeight: "100%",
                        letterSpacing: "1%",
                      }}
                    >
                      {count}
                    </Typography>
                  </Grid2>
                ))
            ) : (
              <Typography textAlign="center" sx={{ width: "100%", mt: 2 }}>
                No Data Available
              </Typography>
            )}
          </Grid2>
        </Paper>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            mt: 3,
          }}
        >
          <Box>
            <span className="appList">Applications List </span>
            <span className="appList" style={{ fontWeight: "300" }}>
              {" "}
              {fetchOverList?.catalogOverviewAppDTO?.totalAppsCount || 0}
            </span>
          </Box>
          <TextField
            placeholder="Search Apps"
            variant="outlined"
            size="small"
            value={tempSearchQuery}
            onChange={(e) => setTempSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearchQuery(tempSearchQuery);
                setPage(0);
              }
            }}
            sx={{
              width: "225px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                paddingLeft: "8px",
                "& fieldset": { borderColor: "#231F2033" },
                "&:hover fieldset": { borderColor: "#231F2033" },
                "&.Mui-focused fieldset": { borderColor: "#231F2033" },
              },
              "& .MuiInputBase-input": { padding: "10px 8px" },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "20px", color: "#717273" }} />
                  </InputAdornment>
                ),
                endAdornment: tempSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTempSearchQuery("");
                        setSearchQuery("");
                        setPage(0);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>

        <DataGrid
          rows={appData}
          onRowSelectionModelChange={handleRowSelected}
          rowSelectionModel={appSelected}
          columns={columns}
          getRowId={(row) => row.id}
          pagination
          pageSizeOptions={[10, 15, 20]}
          paginationMode="server"
          rowCount={fetchOverList?.catalogOverviewAppDTO?.totalAppsCount || 0}
          paginationModel={{ page, pageSize }}
          disableColumnMenu={true}
          hideFooterSelectedRowCount
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          loading={false}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "Items per page", // Remove the extra space
            },
            // Add these additional overrides to ensure consistency
            footerPaginationRowsPerPage: "Items per page",
            footerRowSelected: (count) =>
              `${count} item${count === 1 ? "" : "s"} selected`,
          }}
          sx={{
            height: "auto",
            "& .MuiDataGrid-columnHeader": {
              fontSize: "14px",
              fontWeight: "600",
              backgroundColor: "#f0f0f0 !important",
              color: "#121415",
              lineHeight: "30px",
              height: "48px !important",
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14px",
              fontWeight: "600",
              //alignItems: "center",
              //justifyContent: "center",
              minHeight: "30px",
              lineHeight: "30px",
              padding: "0",
              marginLeft: "0",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid rgba(224, 224, 224, 1)",
              fontSize: "14px",
              lineHeight: "20px",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              padding: "8px 12px",
              minHeight: "52px !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
            "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
            "& .MuiDataGrid-container--top [role=row]": {
              background: "#f0f0f0 !important",
              height: "48px !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              marginTop: "0px !important",
            },
            // Add this CSS override to ensure the text shows correctly
            "& .MuiTablePagination-displayedRows": {
              fontSize: "14px",
            },
            "& .MuiTablePagination-selectLabel": {
              fontSize: "14px",
            },
          }}
          slotProps={{
            columnHeader: {
              sx: {
                "& .MuiDataGrid-sortIcon": {
                  opacity: "1 !important",
                  visibility: "visible !important",
                },
              },
            },
            // Add pagination slot props to override the text
            pagination: {
              labelRowsPerPage: "Items per page",
              showFirstButton: true,
              showLastButton: true,
            },
          }}
        />

        <div>&nbsp;&nbsp;</div>
      </Box>
      <OEMDrawerFilter
        openFilter={openFilter}
        toggleDrawer={toggleDrawer}
        categoryObjectList={categoryObjectList}
        modelObjectList={modelObjectList}
        handleFilterApiCalls={handleFilterApiCalls}
        channelListchecked={channelListchecked}
        setChannelListchecked={setChannelListchecked}
        channelSelections={channelSelections}
        setChannelSelections={setChannelSelections}
        categorySelection={categorySelection}
        setCategorySelection={setCategorySelection}
        modelSelections={modelSelections}
        setModelSelections={setModelSelections}
        paymentSelections={paymentSelections}
        setPaymentSelections={setPaymentSelections}
        countrySelections={countrySelections}
        setCountrySelections={setCountrySelections}
        statusSelections={statusSelections}
        setStatusSelections={setStatusSelections}
        apiLevelSelections={apiLevelSelections}
        setApiLevelSelections={setApiLevelSelections}
        ecuTypeSelections={ecuTypeSelections}
        setEcuTypeSelections={setEcuTypeSelections}
        keepMounted
      />
      <Modal open={openPopUp} onClose={() => setOpenPopUp(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 200,
            bgcolor: "background.paper",

            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{ mb: 2, color: "#2a2c2d", fontSize: "24px", fontWeight: 600 }}
          >
            No Data Found
          </Typography>
          <Typography sx={{ mb: 3, color: "#717273", fontSize: "18px" }}>
            No data found for the selected filters.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setOpenPopUp(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
